import React, { ReactNode, useEffect, useMemo, useState } from "react";
import { Col, DatePicker, Divider, Form, Input, Row, Select, Spin, Tooltip } from "antd";
import moment from "moment";
import { FormInstance } from "antd/es/form/Form";
import { useAppDispatch, useAppSelector } from "../../../..";
import {
  checkIsLoading,
  displayFormat,
  getDictionaryItemById,
  getSelectOptions,
  toDisplayFormat,
  toHttpFormat,
  useActiveModal,
} from "../../../../lib/utils";
import { IPolicy } from "../../types/IPolicy";
import { ReactComponent as LoupeIcon } from "../../../../img/loupe-icon.svg";
import { OfferListModal } from "../../../../components/OfferList/OfferListModal/OfferListModal";
import { useOfferList } from "../../../../components/OfferList/utils/useOfferList";
import { IChangingMode, Offer } from "../../../../lib/types";
import { IPolicySearch } from "../../types/IPolicySearch";
import { fetchOfferById } from "../../store/slice/policiesSlice";
import { IChangingRecordKeyNumber } from "../../../../lib/types/IViewMode";
import { ReactComponent as EditIcon } from "../../../../img/versions_icons/editVersion_icon.svg";
import { ReactComponent as AddVersionIcon } from "../../../../img/versions_icons/addVersion_icon.svg";
import { ReactComponent as CloseVersionIcon } from "../../../../img/versions_icons/closeVersion_icon.svg";
import { ReactComponent as CancelButtonIcon } from "../../../../img/buttons_icons/cancelButton_icon.svg";
import { ReactComponent as SubmitButtonIcon } from "../../../../img/buttons_icons/submitButton_icon.svg";
import { hasAccess } from "../../../../components/RolesModule/hasAccess";
import {
  MENU_ADMIN_OFFERING_POLICY_WRITE,
  POLICY_EXTENDED_EDIT,
} from "../../../../lib/const/permissions";
import "./PolicyParams.less";

interface ComponentProps {
  mode: "create" | "view" | "edit_close_date" | "edit";
  childChangingMode?: IChangingRecordKeyNumber;
  policy?: IPolicy;
  policyParamsForm?: FormInstance<IPolicySearch>;
  isLoading?: boolean;
  isChild?: boolean;
  idx?: number;
  isLatestVersion?: boolean;
  isEditingDisabled?: boolean;
  onChangePolicyType?: (id: number) => void;
  changeViewMode?: (params?: IChangingRecordKeyNumber) => void;
  onCancel?: () => void;
  onSubmit?: () => void;
  setActivePolicyVersion?: (value: React.SetStateAction<IPolicy | undefined>) => void;
}

export const PolicyParams = ({
  mode,
  childChangingMode,
  policy,
  policyParamsForm,
  isLoading = false,
  isChild = false,
  idx,
  isLatestVersion,
  isEditingDisabled,
  onChangePolicyType,
  changeViewMode,
  onCancel,
  onSubmit,
  setActivePolicyVersion,
}: ComponentProps) => {
  const {
    policiesTypesDic,
    policiesTypesDicLoading,
    policiesActionsDic,
    policiesActionsDicLoading,
    policiesContextsDic,
    policiesContextsDicLoading,
    policiesStatusDic,
    policiesStatusDicLoading,
    updatePolicyLoading,
    updateCloseDatePolicyLoading,
  } = useAppSelector((state) => state.policies);

  const dispatch = useAppDispatch();

  const { activeModal, setActiveModal } = useActiveModal<"" | "actorId">("");

  const [selectedNewOfferView, setSelectedNewOfferView] = useState<Offer>();

  useEffect(() => {
    if (policy?.info) {
      const newOffering = JSON.parse(policy.info)?.newOffering;

      if (newOffering) {
        dispatch(fetchOfferById({ id: newOffering, onDate: toHttpFormat(moment()) })).then(
          (res) => {
            setSelectedNewOfferView(res?.payload as Offer);
          }
        );
      }
    }
    return () => {
      setSelectedNewOfferView(undefined);
    };
  }, [policy]);

  const closeModal = () => {
    setActiveModal("");
  };

  const offerListProps = useOfferList({
    initialOfferListViewMode: "selectOffer",
    initialSearchValues: { onDate: moment(), page: 0, size: 10 },
  });

  const submitOfferListModal = (offer: Offer) => {
    policyParamsForm &&
      policyParamsForm.setFieldsValue({
        newOffering: offer.id,
        newOfferingName: offer.extName,
      });
    offerListProps.onResetSearch();
    closeModal();
  };

  const isCurrentVersion = useMemo(() => {
    const fromDate = policy?.fromDate && moment(policy.fromDate);
    const toDate = policy?.toDate && moment(policy.toDate);
    if (fromDate && toDate) {
      return moment().isBetween(fromDate, toDate);
    }
    return false;
  }, [policy]);

  const isPastVersion = useMemo(() => {
    const fromDate = policy?.fromDate && moment(policy.fromDate);
    const toDate = policy?.toDate && moment(policy.toDate);
    if (fromDate && toDate) {
      return toDate.isBefore(moment());
    }
    return false;
  }, [policy]);

  const renderDataItem = (props: {
    label: string;
    data?: ReactNode;
    offsetCol?: number;
    spanCol?: number;
    labelCol?: number;
    wrapperCol?: number;
  }): ReactNode => {
    const { label, data, offsetCol = 0, spanCol = 6, labelCol = 8, wrapperCol = 16 } = props;
    return (
      <Col offset={offsetCol} span={spanCol}>
        <Row className="policy-params__dataRow">
          <Col span={labelCol}>
            <label>{label}</label>
          </Col>
          <Col span={wrapperCol}>
            <span>{data}</span>
          </Col>
        </Row>
      </Col>
    );
  };

  const handleSetViewMode = (
    e: React.MouseEvent<SVGSVGElement, MouseEvent>,
    mode: IChangingMode
  ) => {
    e.stopPropagation();

    if (mode === "edit") {
      if (policyParamsForm) {
        policyParamsForm.setFieldsValue({
          name: policy?.name,
          actionId: policy?.actionId,
          dsc: policy?.dsc,
          typeId: policy?.typeId,
          fromDate: moment(policy?.fromDate),
          toDate: moment(policy?.toDate),

          assocTypeId: policy?.assocTypeId,
          assocObjectId: policy?.assocObjectId,
          actorTypeId: policy?.actorTypeId,
          actorId: policy?.actorId,
          statusId: policy?.status,

          newOffering: selectedNewOfferView?.id,
          newOfferingName: selectedNewOfferView?.extName,
        });
      }
    }
    if (mode === "add") {
      if (policyParamsForm) {
        policyParamsForm.setFieldsValue({
          name: policy?.name,
          actionId: policy?.actionId,
          dsc: policy?.dsc,
          typeId: policy?.typeId,
          fromDate: moment(policy?.fromDate).add(1, "d"),
          toDate: moment(policy?.toDate),

          assocTypeId: policy?.assocTypeId,
          assocObjectId: policy?.assocObjectId,
          actorTypeId: policy?.actorTypeId,
          actorId: policy?.actorId,
          statusId: policy?.status,

          newOffering: selectedNewOfferView?.id,
          newOfferingName: selectedNewOfferView?.extName,
        });
      }
    }
    if (mode === "close") {
      if (policyParamsForm) {
        policyParamsForm.setFieldsValue({
          toDate: moment(policy?.toDate),
          fromDate: moment(policy?.fromDate),
        });
      }
    }
    setActivePolicyVersion && setActivePolicyVersion(policy);
    changeViewMode && idx !== undefined && changeViewMode([idx, mode]);
  };

  const handleOnCancel = (e: React.MouseEvent<SVGSVGElement, MouseEvent>) => {
    e.stopPropagation();

    if (policyParamsForm) {
      policyParamsForm.setFieldsValue({
        name: policy?.name,
        actionId: policy?.actionId,
        dsc: policy?.dsc,
        typeId: policy?.typeId,
        fromDate: moment(policy?.fromDate),
        toDate: moment(policy?.toDate),
        assocTypeId: policy?.assocTypeId,
        assocObjectId: policy?.assocObjectId,
        actorTypeId: policy?.actorTypeId,
        actorId: policy?.actorId,
        statusId: policy?.status,
      });

      onChangePolicyType && policy?.typeId && onChangePolicyType(policy?.typeId);

      onCancel && onCancel();
    }
  };

  const handleOnSubmit = (e: React.MouseEvent<SVGSVGElement, MouseEvent>) => {
    e.stopPropagation();
    onSubmit && onSubmit();
  };

  const renderButtons = () => {
    if (mode === "edit" || mode === "edit_close_date") {
      return (
        <Row
          align="middle"
          className={`policy-params__${
            mode === "edit_close_date" ? "edit__close__btns" : "edit__btns"
          }`}
        >
          <Col>
            <Tooltip title="Подтвердить">
              <SubmitButtonIcon
                onClick={handleOnSubmit}
                className={`${
                  checkIsLoading(updatePolicyLoading, updateCloseDatePolicyLoading)
                    ? "pointer-event-none"
                    : ""
                }`}
              />
            </Tooltip>
          </Col>
          <Col>
            <Tooltip title="Отменить">
              <CancelButtonIcon onClick={handleOnCancel} />
            </Tooltip>
          </Col>
        </Row>
      );
    }

    return isChild && !isEditingDisabled && hasAccess(MENU_ADMIN_OFFERING_POLICY_WRITE) ? (
      <Row gutter={7} justify="end" className="policy-params__btns">
        {isLatestVersion && (
          <Col>
            <CloseVersionIcon
              onClick={(e) => handleSetViewMode(e, "close")}
              className="policy-params__iconBtn"
            />
          </Col>
        )}
        {!isPastVersion && (
          <>
            <Col>
              {isLatestVersion && (
                <Tooltip title="Добавить версию">
                  <AddVersionIcon
                    onClick={(e) => handleSetViewMode(e, "add")}
                    className="policy-params__iconBtn"
                  />
                </Tooltip>
              )}
            </Col>
            <Col>
              <Tooltip title="Редактировать">
                <EditIcon
                  onClick={(e) => handleSetViewMode(e, "edit")}
                  className="policy-params__iconBtn"
                />
              </Tooltip>
            </Col>
          </>
        )}
      </Row>
    ) : null;
  };

  const childClassName = useMemo(() => {
    if (!isChild) return "";
    if (isCurrentVersion) return `row_child row_current-version`;
    if (isPastVersion) return "row_child row_past-version";
    return "row_child";
  }, [policy, isChild]);

  return (
    <Row className={`policy-params ${childClassName}`} style={{ width: "100%" }}>
      {mode !== "create" && <Divider style={{ margin: "0 0 9px" }} />}
      <Spin spinning={isLoading}>
        <Row gutter={50} style={{ width: "100%" }}>
          {mode === "create" || mode === "edit" ? (
            <Col span={6}>
              <Form.Item
                label="Наименование"
                name="name"
                labelCol={{ span: 8 }}
                wrapperCol={{ span: 16 }}
                rules={[
                  {
                    required: true,
                    message: "",
                  },
                ]}
                className={mode === "edit" ? "not-bottom" : ""}
              >
                <Input
                  maxLength={255}
                  style={{ width: 220 }}
                  placeholder="Введите текст..."
                  allowClear
                />
              </Form.Item>
            </Col>
          ) : (
            <>{renderDataItem({ label: "Наименование", data: policy?.name })}</>
          )}
          {mode === "create" ? (
            <Col span={6}>
              <Form.Item
                label="Контекст"
                name="contextId"
                labelCol={{ span: 8 }}
                wrapperCol={{ span: 16 }}
                rules={[
                  {
                    required: true,
                    message: "",
                  },
                ]}
              >
                <Select
                  options={getSelectOptions({
                    list: policiesContextsDic,
                  })}
                  loading={checkIsLoading(policiesContextsDicLoading)}
                  style={{ width: 200 }}
                  placeholder="Выбрать"
                  allowClear
                />
              </Form.Item>
            </Col>
          ) : (
            <>
              {renderDataItem({
                label: "Контекст",
                data: getDictionaryItemById(policiesContextsDic, policy?.contextId)?.name,
              })}
            </>
          )}

          {mode === "create" || (mode === "edit" && childChangingMode?.[1] === "add") ? (
            <Col span={6}>
              <Form.Item dependencies={["toDate"]} noStyle>
                {({ getFieldValue }) => {
                  const toDate = getFieldValue("toDate");
                  const fromDate = getFieldValue("fromDate");
                  return (
                    <Form.Item
                      label="Период с"
                      name="fromDate"
                      labelCol={{ span: 8 }}
                      wrapperCol={{ span: 16 }}
                      rules={[
                        {
                          required: true,
                          message: "",
                        },
                      ]}
                      className={mode === "edit" ? "not-bottom" : ""}
                    >
                      <DatePicker
                        style={{ width: 114 }}
                        allowClear
                        disabledDate={(current) => {
                          if (mode === "edit") {
                            return (
                              current.isAfter(moment(toDate), "day") ||
                              current.isBefore(moment(policy?.fromDate).add(1, "d"), "day")
                            );
                          }
                          return toDate ? current.isAfter(toDate, "day") : false;
                        }}
                        format={displayFormat}
                      />
                    </Form.Item>
                  );
                }}
              </Form.Item>
            </Col>
          ) : (
            <>
              {renderDataItem({
                label: "Период с",
                data: policy?.fromDate && toDisplayFormat(policy?.fromDate),
              })}
            </>
          )}

          {mode === "create" || mode === "edit" ? (
            <Col span={6}>
              <Form.Item
                label="Комментарий"
                name="dsc"
                labelCol={{ span: 8 }}
                wrapperCol={{ span: 16 }}
                className={mode === "edit" ? "not-bottom" : ""}
              >
                <Input
                  style={{ width: 220 }}
                  maxLength={255}
                  placeholder="Введите текст..."
                  allowClear
                />
              </Form.Item>
              {renderButtons()}
            </Col>
          ) : (
            <>
              {renderDataItem({
                label: "Комментарий",
                data: (
                  <Row>
                    <Col span={11}>{policy?.dsc}</Col>
                    <Col span={13}>{renderButtons()}</Col>
                  </Row>
                ),
              })}
            </>
          )}
        </Row>

        <Row gutter={50} style={{ width: "100%" }}>
          {mode === "create" || mode === "edit" ? (
            <Col span={6}>
              <Form.Item
                label="Тип"
                name="typeId"
                labelCol={{ span: 8 }}
                wrapperCol={{ span: 16 }}
                rules={[
                  {
                    required: true,
                    message: "",
                  },
                ]}
                className={mode === "edit" ? "not-bottom" : ""}
              >
                <Select
                  options={getSelectOptions({
                    list: policiesTypesDic,
                  })}
                  loading={checkIsLoading(policiesTypesDicLoading)}
                  style={{ width: 220 }}
                  placeholder="Выбрать"
                  allowClear
                  onChange={(e) => {
                    onChangePolicyType && onChangePolicyType(e as number);
                    policyParamsForm &&
                      policyParamsForm.resetFields(["actionId", "newOfferingName"]);
                  }}
                  disabled={mode === "edit" && !hasAccess(POLICY_EXTENDED_EDIT)}
                />
              </Form.Item>
            </Col>
          ) : (
            <>
              {renderDataItem({
                label: "Тип",
                data: getDictionaryItemById(policiesTypesDic, policy?.typeId)?.name,
              })}
            </>
          )}

          {mode === "create" || mode === "edit" ? (
            <Col span={6}>
              <Form.Item dependencies={["typeId"]} noStyle>
                {({ getFieldValue }) => {
                  const typeId = getFieldValue("typeId");
                  // console.log({ typeId });

                  return (
                    <Form.Item
                      label="Действие"
                      name="actionId"
                      labelCol={{ span: 8 }}
                      wrapperCol={{ span: 16 }}
                      rules={[
                        {
                          required: typeId !== 3,
                          message: "",
                        },
                      ]}
                      className={mode === "edit" ? "not-bottom" : ""}
                    >
                      <Select
                        options={getSelectOptions({
                          list: policiesActionsDic.filter(
                            ({ parentDicId }) => Number(parentDicId) === Number(typeId)
                          ),
                        })}
                        loading={checkIsLoading(policiesActionsDicLoading)}
                        style={{ width: 200 }}
                        placeholder="Выбрать"
                        allowClear
                        disabled={!typeId}
                      />
                    </Form.Item>
                  );
                }}
              </Form.Item>
            </Col>
          ) : (
            <>
              {renderDataItem({
                label: "Действие",
                data: getDictionaryItemById(policiesActionsDic, policy?.actionId)?.name,
              })}
            </>
          )}

          {mode === "create" || mode === "edit_close_date" ? (
            <Col span={6}>
              <Form.Item dependencies={["fromDate"]} noStyle>
                {({ getFieldValue }) => {
                  const fromDate = getFieldValue("fromDate");
                  const toDate = getFieldValue("toDate");
                  return (
                    <Form.Item
                      label="Период по"
                      name="toDate"
                      labelCol={{ span: 8 }}
                      wrapperCol={{ span: 16 }}
                      rules={[
                        {
                          required: mode === "edit_close_date",
                          message: "",
                        },
                      ]}
                      className={mode === "edit_close_date" ? "not-bottom" : ""}
                    >
                      <DatePicker
                        style={{ width: 114 }}
                        allowClear
                        disabledDate={(current) => {
                          if (mode === "edit_close_date") {
                            return fromDate
                              ? current.isBefore(fromDate, "day") || current.isAfter(toDate, "day")
                              : false;
                          }
                          return fromDate ? current.isBefore(fromDate, "day") : false;
                        }}
                        format={displayFormat}
                      />
                    </Form.Item>
                  );
                }}
              </Form.Item>
            </Col>
          ) : (
            <>
              {renderDataItem({
                label: "Период по",
                data: policy?.toDate && toDisplayFormat(policy?.toDate),
              })}
            </>
          )}

          {mode === "create" || mode === "edit" ? (
            <Col span={6}>
              <Form.Item
                label="Статус"
                name="statusId"
                labelCol={{ span: 8 }}
                wrapperCol={{ span: 16 }}
                rules={[
                  {
                    required: true,
                    message: "",
                  },
                ]}
                className={mode === "edit" ? "not-bottom" : ""}
              >
                <Select
                  options={getSelectOptions({
                    list: policiesStatusDic,
                  })}
                  loading={checkIsLoading(policiesStatusDicLoading)}
                  style={{ width: 220 }}
                  placeholder="Выбрать"
                  allowClear
                />
              </Form.Item>
            </Col>
          ) : (
            <>
              {renderDataItem({
                label: "Статус",
                data: getDictionaryItemById(policiesStatusDic, policy?.status)?.name,
              })}
            </>
          )}
        </Row>

        <Row gutter={50} style={{ width: "100%" }}>
          {mode === "create" || mode === "edit" ? (
            <Col span={6}>
              <Form.Item dependencies={["actionId"]} noStyle>
                {({ getFieldValue }) => {
                  const actionId = getFieldValue("actionId");
                  return (
                    <>
                      {actionId === 5 ? (
                        <Form.Item
                          style={{ width: "100%" }}
                          className="form-item-required__hidden-error__mb0 policy-params__parameter"
                          label={<div>Конвертировать в :</div>}
                        >
                          <Row>
                            <Col>
                              <Form.Item
                                name="newOfferingName"
                                className="form-item-required__hidden-error__mb0 policy-params__parameter"
                                rules={[
                                  {
                                    required: true,
                                    message: "",
                                  },
                                ]}
                                style={{ width: "100%", maxWidth: 220 }}
                              >
                                <Input allowClear placeholder="Выберите предложение" disabled />
                              </Form.Item>
                            </Col>
                            <Col span={3}>
                              <Row wrap={false}>
                                <LoupeIcon
                                  className="policy-table__loupeIcon"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setActiveModal("actorId");
                                  }}
                                />
                              </Row>
                            </Col>
                          </Row>
                        </Form.Item>
                      ) : null}
                    </>
                  );
                }}
              </Form.Item>
            </Col>
          ) : (
            <>
              {Number(policy?.actionId) === 5
                ? renderDataItem({
                    label: "Конвертировать в",
                    data: selectedNewOfferView?.extName || "",
                  })
                : null}
            </>
          )}
        </Row>
      </Spin>

      <OfferListModal
        visible={activeModal === "actorId"}
        onOk={submitOfferListModal}
        onCancel={closeModal}
        offerListProps={{ ...offerListProps, filterName: "group-offer" }}
      />
    </Row>
  );
};

export default PolicyParams;
